import get from "lodash/get";
import set from "lodash/set";
import omit from "lodash/omit";
import { Kinds } from "constants/product-collection";
import { AllocationDimensionKind } from "helpers/allocation-dimension";
import { Map } from "immutable";
import { ASSORTMENT_FIELD_NAME, ASSORTMENT_NAME_DELIMITER } from "helpers/server-rollup";

const ASSORTMENT_DISPLAY_NAME = "Assortment Name";
const ASSORTMENT_FIELD_WIDTH = 230;

export const deleteAllocationsValue = (values: any[]) => {
    return values.map((value) => ({
        ...value,
        struct_value: omit(value.struct_value || {}, "allocations"),
    }));
};

export const setAssortmentName = (values: any[], collection: any) => {
    const assortmentId = collection.id;
    const assortmentName = collection.name;

    return values.map((value) => ({
        ...value,
        struct_value: {
            ...(value?.struct_value || {}),
            fields: {
                ...(value?.struct_value?.fields || {}),
                [ASSORTMENT_FIELD_NAME]: {
                    string_value: `${assortmentName}${ASSORTMENT_NAME_DELIMITER}${assortmentId}`,
                },
            },
        },
    }));
};

export const aggregateDataToBaseCollection = (
    baseCollection: any,
    collections: any,
    fieldPath: string,
    aggregateFn: (acc, value, collection) => any
) => {
    const aggregatedValue = collections?.reduce(
        (acc, it) => {
            acc = aggregateFn(acc, get(it, fieldPath), it);
            return acc;
        },
        get(baseCollection, fieldPath)
    );

    set(baseCollection, fieldPath, aggregatedValue);
};

type RecursiveImmutableMap = Map<string, RecursiveImmutableMap>;
export const aggregateCollectionAllocationDimensions = (collection: any, collections: any[]) => {
    const allocationDimensionsOverCollections = collections.map((c) => c.snapshot.allocation_dimensions);
    let allocationDimensionsMap: RecursiveImmutableMap = Map();
    allocationDimensionsOverCollections.forEach((allocationDimensionContent) => {
        if (!allocationDimensionContent) {
            return;
        }
        Object.entries(allocationDimensionContent).forEach(([allocationKey, allocationStruct]: any[]) => {
            // We don't touch Sizes allocation dimension
            if (
                allocationStruct?.mutable_mixed?.objects?.dimension_values?.list?.values &&
                allocationStruct.mutable_mixed.objects.field_map?.map?.fields?.subtype?.string_value ===
                    AllocationDimensionKind.Generic
            ) {
                allocationStruct.mutable_mixed.objects.dimension_values.list.values.forEach((dimensionValue) => {
                    if (
                        !allocationDimensionsMap.hasIn([allocationKey, dimensionValue.struct_value.id]) &&
                        dimensionValue.struct_value.active &&
                        dimensionValue.struct_value.included
                    ) {
                        allocationDimensionsMap = allocationDimensionsMap.setIn(
                            [allocationKey, dimensionValue.struct_value.id],
                            dimensionValue
                        );
                    }
                });
            }
        });
    });
    if (!collection.snapshot.allocation_dimensions && allocationDimensionsMap.size) {
        collection.snapshot.allocation_dimensions = {};
    }
    allocationDimensionsMap.forEach((allocationValue, key) => {
        set(
            collection.snapshot.allocation_dimensions,
            [key, "mutable_mixed", "objects", "dimension_values", "list", "values"],
            Object.values(allocationValue.toJS())
        );
    });
    return collection;
};

export const addAssortmentNameField = (baseCollection: any) => {
    // Add schema
    baseCollection?.snapshot?.root?.mutable_map?.fields?.item_display_property?.struct_value?.keys?.splice(1, 0, {
        detail_view: {
            display: true,
            editable: false,
        },
        display: {
            enable_aggregation: true,
            enable_group_by: true,
            enable_pivot: true,
            hidden: false,
            read_only: true,
        },
        display_name: ASSORTMENT_DISPLAY_NAME,
        field_name: ASSORTMENT_FIELD_NAME,
        grid_column: {
            pinned: "UNPINNED",
            show_filter_set: true,
            width: ASSORTMENT_FIELD_WIDTH,
        },
        hidden: false,
        kind: Kinds.ASSORTMENT_LINK_KIND,
        value_type: "STRING",
        field_value_delimiter: ASSORTMENT_NAME_DELIMITER,
    });

    // set field position in the grid
    baseCollection?.snapshot?.item_display_preferences?.mutable_list?.values?.forEach((value) => {
        if (value.struct_value?.display_preferences) {
            value.struct_value?.display_preferences?.splice(1, 0, {
                display: {
                    hidden: false,
                },
                field_name: ASSORTMENT_FIELD_NAME,
                grid_column: {
                    pinned: "UNPINNED",
                    width: ASSORTMENT_FIELD_WIDTH,
                },
            });
        } else {
            value.struct_value = {
                ...value.struct_value,
                display_preferences: [
                    {
                        display: {
                            hidden: false,
                        },
                        field_name: ASSORTMENT_FIELD_NAME,
                        grid_column: {
                            pinned: "UNPINNED",
                            width: ASSORTMENT_FIELD_WIDTH,
                        },
                    },
                ],
            };
        }
    });
};

const getSelectedValuesForDimension = (collections: any, dimension: string) => {
    return collections.reduce((acc, col) => {
        const dimensionValues =
            col?.snapshot?.allocation_dimensions?.[dimension]?.mutable_mixed?.objects?.dimension_values?.list?.values ||
            [];
        return dimensionValues.reduce((accv, value) => {
            value.struct_value.included && accv.add(value.struct_value.id);
            return accv;
        }, acc);
    }, new Set<string>());
};

const enableAllSelectedValuesForDimension = (baseCollection: any, collections: any, dimensionFieldName: string) => {
    const dimension = getSelectedValuesForDimension(collections, dimensionFieldName);

    baseCollection?.snapshot?.allocation_dimensions?.[
        dimensionFieldName
    ]?.mutable_mixed?.objects?.dimension_values?.list?.values?.forEach((value) => {
        value.struct_value.included = dimension.has(value.struct_value.id);
    });
};

export const enableAllSelectedDimensions = (baseCollection: any, collections: any) => {
    const allocationDimensionFieldNames = Object.keys(get(baseCollection, "snapshot.allocation_dimensions", {})).filter(
        (dimensionKey) => {
            const subType =
                baseCollection?.snapshot?.allocation_dimensions?.[dimensionKey]?.mutable_mixed?.objects?.field_map?.map
                    ?.fields?.subtype?.string_value || "";
            return subType === AllocationDimensionKind.Generic;
        }
    );

    allocationDimensionFieldNames.forEach((dimensionFieldName) =>
        enableAllSelectedValuesForDimension(baseCollection, collections, dimensionFieldName)
    );
};
